"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Award, FileCheck2, Phone } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
export default function CallAction() {
  const shouldReduceMotion = useReducedMotion();
  const trustHighlights = [{
    icon: FileCheck2,
    title: "Brookhaven zoning handled",
    subtitle: "Permitting & setback management"
  }, {
    icon: Award,
    title: "Craftsmanship coverage",
    subtitle: "Comprehensive post-project warranty"
  }];
  const secondaryGuides = [{
    title: "Home Additions Guide",
    to: "/home-additions/"
  }, {
    title: "Basement Remodeling Guide",
    to: "/basement-remodeling/"
  }, {
    title: "Outdoor Living Guide",
    to: "/outdoor-living/"
  }, {
    title: "Deck Remodeling Guide",
    to: "/deck-remodeling/"
  }];
  return <section id="call-to-action" data-nav="light" className="bg-background text-foreground py-20 md:py-24">
      <div className="mx-auto max-w-7xl px-5 sm:px-9 lg:px-16">
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 15
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }} className="relative overflow-hidden rounded-2xl bg-secondary text-secondary-foreground shadow-2xl">
          {/* Subtle architectural hairline accents */}
          <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/60 to-transparent" />
          <div aria-hidden="true" className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 rounded-full bg-primary/10 blur-3xl" />

          <div className="relative z-10 px-6 py-12 sm:px-12 sm:py-16 md:px-16 lg:py-20">
            <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12">
              {/* Left narrative content */}
              <div className="lg:col-span-7">
                <div className="mb-4">
                  <Badge variant="outline" className="border-primary/40 bg-primary/10 px-3.5 py-1 text-xs font-semibold uppercase tracking-wider text-primary">
                    Brookhaven Bespoke Additions & Living
                  </Badge>
                </div>

                <h2 className="font-serif text-3xl font-semibold tracking-tight text-secondary-foreground sm:text-4xl md:text-5xl">
                  Expand your Brookhaven home with confidence.
                </h2>

                <p className="mt-5 max-w-xl text-base leading-relaxed text-secondary-foreground/80 sm:text-lg">
                  Schedule an on-site design consultation to review your property,
                  local zoning rules, and realistic investment estimates with our
                  builders.
                </p>

                {/* Primary CTA and Phone link */}
                <div className="mt-8 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
                  <Button asChild size="lg" className="h-12 bg-primary px-8 text-base font-semibold text-primary-foreground shadow-md transition-[background-color,box-shadow,transform] duration-200 hover:bg-primary/90 hover:shadow-lg active:scale-[0.98]">
                    <Link to="/ContactRequestAnEstimatePage" className="inline-flex items-center gap-2">
                      <span>Request an estimate</span>
                      <ArrowRight className="size-4" />
                    </Link>
                  </Button>

                  <a href="tel:+14708479076" className="inline-flex h-12 items-center gap-2.5 rounded-lg border border-secondary-foreground/20 bg-secondary-foreground/5 px-5 text-sm font-medium text-secondary-foreground transition-colors hover:border-primary hover:bg-secondary-foreground/10 focus-visible:border-ring focus-visible:outline-none" aria-label="Call Brookhaven Elevation Renovations at (470) 847-9076">
                    <Phone className="size-4 text-primary" />
                    <span>Call (470) 847-9076</span>
                  </a>
                </div>

                {/* Secondary guides outline links */}
                <div className="mt-8 border-t border-secondary-foreground/15 pt-6">
                  <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-secondary-foreground/60">
                    Explore related planning guides:
                  </p>
                  <div className="flex flex-wrap items-center gap-2.5">
                    {secondaryGuides.map((guide, index) => <Link key={guide.title} data-index={index} to={guide.to} className="inline-flex items-center gap-1.5 rounded-lg border border-secondary-foreground/20 bg-secondary-foreground/5 px-3 py-1.5 text-xs font-medium text-secondary-foreground/90 transition-colors hover:border-primary/50 hover:bg-secondary-foreground/10 hover:text-secondary-foreground focus-visible:border-ring focus-visible:outline-none">
                        <span>{guide.title}</span>
                        <ArrowRight className="size-3 text-primary/75" />
                      </Link>)}
                  </div>
                </div>
              </div>

              {/* Right column: Atelier glass plinths highlighting credentials */}
              <div className="flex flex-col gap-4 lg:col-span-5">
                {trustHighlights.map((item, index) => {
                const IconComponent = item.icon;
                return <div key={item.title} data-index={index} className="rounded-xl border border-secondary-foreground/15 bg-secondary-foreground/5 p-4.5 backdrop-blur-md transition-[border-color,background-color] duration-200 hover:border-primary/40 hover:bg-secondary-foreground/10">
                      <div className="flex items-start gap-3.5">
                        <div className="rounded-lg bg-primary/15 p-2 text-primary">
                          <IconComponent className="size-5" />
                        </div>
                        <div>
                          <h3 className="font-sans text-sm font-semibold text-secondary-foreground">
                            {item.title}
                          </h3>
                          <p className="mt-0.5 text-xs text-secondary-foreground/75">
                            {item.subtitle}
                          </p>
                        </div>
                      </div>
                    </div>;
              })}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}